import { ComponentProps } from 'react';
import { cva } from 'class-variance-authority';

import { cn } from '@/lib/utils';

type FieldSize = 'sm' | 'md' | 'lg';

const FIELD_FOCUS_CLASS_NAME =
  'focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/25';

const FIELD_PLACEHOLDER_TEXT_CLASS_NAMES: Record<FieldSize, string> = {
  sm: 'text-xs',
  md: 'text-xs',
  lg: 'text-caption',
};

const inputVariants = cva(
  `border border-input rounded-md bg-card shadow-xs transition-[color,box-shadow,border-color] outline-none hover:border-muted-foreground/50 ${FIELD_FOCUS_CLASS_NAME} aria-invalid:border-destructive aria-invalid:focus-visible:ring-destructive/20 disabled:cursor-not-allowed disabled:border-border disabled:bg-muted disabled:text-muted-foreground disabled:shadow-none`,
  {
    variants: {
      kind: {
        text: 'text-foreground placeholder:text-muted-foreground',
        trigger:
          'text-foreground data-[state=open]:border-ring data-[state=open]:ring-3 data-[state=open]:ring-ring/25 [&>svg]:size-4 [&>svg]:text-muted-foreground',
      },
      size: {
        sm: 'px-2 text-xs placeholder:text-xs',
        md: 'px-2.5 text-caption placeholder:text-xs',
        lg: 'px-3 text-sm placeholder:text-caption',
      },
      height: { fixed: '', forced: '', min: '', auto: '' },
    },
    compoundVariants: [
      { height: 'fixed', size: 'sm', className: 'h-7' },
      { height: 'fixed', size: 'md', className: 'h-8' },
      { height: 'fixed', size: 'lg', className: 'h-9' },
      { height: 'forced', size: 'sm', className: 'h-7!' },
      { height: 'forced', size: 'md', className: 'h-8!' },
      { height: 'forced', size: 'lg', className: 'h-9!' },
      { height: 'min', size: 'sm', className: 'min-h-7' },
      { height: 'min', size: 'md', className: 'min-h-8' },
      { height: 'min', size: 'lg', className: 'min-h-9' },
    ],
    defaultVariants: { kind: 'text', size: 'md', height: 'fixed' },
  },
);

const Input = (props: ComponentProps<'input'>) => {
  const { className, type, ...restProps } = props;

  return (
    <input
      type={type}
      data-slot="input"
      className={cn(
        'file:text-foreground placeholder:text-muted-foreground placeholder:text-xs selection:bg-primary selection:text-primary-foreground border-input bg-card h-8 w-full min-w-0 rounded-md border px-2.5 py-1 text-sm shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-6 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
        FIELD_FOCUS_CLASS_NAME,
        'aria-invalid:border-destructive',
        className,
      )}
      {...restProps}
    />
  );
};

export { FIELD_PLACEHOLDER_TEXT_CLASS_NAMES, Input, inputVariants };
export type { FieldSize };
